// Locale-specific formatting utilities for Treppan CRM

/**
 * Helper to ensure UTC interpretation for date strings from backend
 * Backend returns dates without timezone indicator, which JS interprets as local time
 * This ensures they're treated as UTC
 */
export const parseUTCDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;
  
  // If no timezone indicator, assume UTC by appending Z
  const dateStr = date.includes('Z') || date.includes('+') || date.includes('-', 10) 
    ? date 
    : date + 'Z';
  return new Date(dateStr);
};

/**
 * Format a number based on locale
 * @param {number} value - The number to format
 * @param {string} locale - The locale code (e.g., 'en', 'ar', 'de')
 * @param {object} options - Intl.NumberFormat options
 * @returns {string} Formatted number
 */
export const formatNumber = (value, locale = 'en', options = {}) => {
  if (value === null || value === undefined) return '-';
  
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
    es: 'es-ES',
    fr: 'fr-FR',
    hi: 'hi-IN',
    de: 'de-DE',
    zh: 'zh-CN',
  };
  
  const fullLocale = localeMap[locale] || locale;
  
  try {
    return new Intl.NumberFormat(fullLocale, options).format(value);
  } catch (e) {
    return value.toString();
  }
};

/**
 * Format currency based on locale
 * @param {number} value - The amount to format
 * @param {string} locale - The locale code
 * @param {string} currency - Currency code (default: AED — Treppan's home currency)
 * @returns {string} Formatted currency
 */
export const formatCurrency = (value, locale = 'en', currency = 'AED') => {
  if (value === null || value === undefined) return '-';

  const localeMap = {
    en: 'en-AE',
    ar: 'ar-AE',
    es: 'es-ES',
    fr: 'fr-FR',
    hi: 'hi-IN',
    de: 'de-DE',
    zh: 'zh-CN',
  };

  // Default currencies per locale. English / Arabic users on this CRM
  // are UAE-based so AED is the right default; other locales keep their
  // regional currencies as a courtesy.
  const defaultCurrencies = {
    en: 'AED',
    ar: 'AED',
    es: 'EUR',
    fr: 'EUR',
    hi: 'INR',
    de: 'EUR',
    zh: 'CNY',
  };

  const fullLocale = localeMap[locale] || locale;
  const targetCurrency = currency || defaultCurrencies[locale] || 'AED';
  
  try {
    return new Intl.NumberFormat(fullLocale, {
      style: 'currency',
      currency: targetCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch (e) {
    // Fallback when Intl.NumberFormat doesn't know the currency code —
    // default to AED (the Treppan home currency) instead of USD.
    return `AED ${value.toLocaleString()}`;
  }
};

/**
 * Format date based on locale
 * @param {Date|string} date - The date to format
 * @param {string} locale - The locale code
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export const formatDate = (date, locale = 'en', options = {}) => {
  if (!date) return '-';
  
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
    es: 'es-ES',
    fr: 'fr-FR',
    hi: 'hi-IN',
    de: 'de-DE',
    zh: 'zh-CN',
  };
  
  const fullLocale = localeMap[locale] || locale;
  
  // Parse date string - ensure UTC interpretation if no timezone specified
  let dateObj;
  if (typeof date === 'string') {
    // If no timezone indicator, assume UTC by appending Z
    const dateStr = date.includes('Z') || date.includes('+') || date.includes('-', 10) 
      ? date 
      : date + 'Z';
    dateObj = new Date(dateStr);
  } else {
    dateObj = date;
  }
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Dubai',
    ...options,
  };
  
  try {
    return new Intl.DateTimeFormat(fullLocale, defaultOptions).format(dateObj);
  } catch (e) {
    return dateObj.toLocaleDateString('en-US', { timeZone: 'Asia/Dubai' });
  }
};

/**
 * Format date and time based on locale
 * @param {Date|string} date - The date/time to format
 * @param {string} locale - The locale code
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (date, locale = 'en') => {
  if (!date) return '-';
  
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
    es: 'es-ES',
    fr: 'fr-FR',
    hi: 'hi-IN',
    de: 'de-DE',
    zh: 'zh-CN',
  };
  
  const fullLocale = localeMap[locale] || locale;
  
  // Parse date string - ensure UTC interpretation if no timezone specified
  let dateObj;
  if (typeof date === 'string') {
    // If no timezone indicator, assume UTC by appending Z
    const dateStr = date.includes('Z') || date.includes('+') || date.includes('-', 10) 
      ? date 
      : date + 'Z';
    dateObj = new Date(dateStr);
  } else {
    dateObj = date;
  }
  
  try {
    return new Intl.DateTimeFormat(fullLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Asia/Dubai'
    }).format(dateObj);
  } catch (e) {
    return dateObj.toLocaleString('en-US', { timeZone: 'Asia/Dubai' });
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {Date|string} date - The date to compare
 * @param {string} locale - The locale code
 * @param {object} translations - Translation object for time units
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, locale = 'en', translations = null) => {
  if (!date) return '-';
  
  // Parse date string - ensure UTC interpretation if no timezone specified
  let dateObj;
  if (typeof date === 'string') {
    // If no timezone indicator, assume UTC by appending Z
    const dateStr = date.includes('Z') || date.includes('+') || date.includes('-', 10) 
      ? date 
      : date + 'Z';
    dateObj = new Date(dateStr);
  } else {
    dateObj = date;
  }
  
  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  
  // Get translated time units if available
  const t = translations?.time || {
    today: 'Today',
    yesterday: 'Yesterday',
    ago: 'ago',
    minutes: 'minutes',
    hours: 'hours',
    days: 'days',
    weeks: 'weeks',
    months: 'months',
  };
  
  // Check if today
  if (dateObj.toDateString() === now.toDateString()) {
    if (diffMins < 1) return t.today || 'Just now';
    if (diffMins < 60) return `${diffMins} ${t.minutes} ${t.ago}`;
    return `${diffHours} ${t.hours} ${t.ago}`;
  }
  
  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateObj.toDateString() === yesterday.toDateString()) {
    return t.yesterday || 'Yesterday';
  }
  
  // Relative days/weeks/months
  if (diffDays < 7) return `${diffDays} ${t.days} ${t.ago}`;
  if (diffWeeks < 4) return `${diffWeeks} ${t.weeks} ${t.ago}`;
  if (diffMonths < 12) return `${diffMonths} ${t.months} ${t.ago}`;
  
  // Fall back to formatted date
  return formatDate(dateObj, locale);
};

/**
 * Format percentage based on locale
 * @param {number} value - The percentage value (0-100)
 * @param {string} locale - The locale code
 * @returns {string} Formatted percentage
 */
export const formatPercent = (value, locale = 'en') => {
  if (value === null || value === undefined) return '-';
  
  const localeMap = {
    en: 'en-US',
    ar: 'ar-SA',
    es: 'es-ES',
    fr: 'fr-FR',
    hi: 'hi-IN',
    de: 'de-DE',
    zh: 'zh-CN',
  };
  
  const fullLocale = localeMap[locale] || locale;
  
  try {
    return new Intl.NumberFormat(fullLocale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value / 100);
  } catch (e) {
    return `${value}%`;
  }
};

/**
 * Get locale-specific number separators
 * @param {string} locale - The locale code
 * @returns {object} Decimal and thousands separators
 */
export const getNumberSeparators = (locale = 'en') => {
  const separators = {
    en: { decimal: '.', thousands: ',' },
    ar: { decimal: '٫', thousands: '٬' },
    es: { decimal: ',', thousands: '.' },
    fr: { decimal: ',', thousands: ' ' },
    hi: { decimal: '.', thousands: ',' },
    de: { decimal: ',', thousands: '.' },
    zh: { decimal: '.', thousands: ',' },
  };
  
  return separators[locale] || separators.en;
};

export default {
  parseUTCDate,
  formatNumber,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatPercent,
  getNumberSeparators,
};
